// Plan markdown section model — split by H2, github-slugger-style identity,
// per-section diff + three-way merge for stale sign-off (docs/design/plan-tab/02).
// Section-granular only — no word-level merge.

export interface PlanSection {
  /** Heading slug (github-slugger style). Preamble before first H2 = "introduction". */
  slug: string;
  /** Display heading without ##; "Introduction" for the preamble. */
  heading: string;
  /** Full section body including the ## heading line (preamble has no heading line). */
  body: string;
}

export type SectionChangeKind = "added" | "removed" | "changed" | "unchanged";

export interface SectionDiff {
  slug: string;
  heading: string;
  kind: SectionChangeKind;
  baseBody?: string;
  proposedBody?: string;
}

export type MergeChoice = "proposed" | "current";

export interface SectionMergeResult {
  slug: string;
  heading: string;
  status: "take_proposed" | "take_current" | "unchanged" | "conflict" | "added" | "removed";
  /** Body to use when not a conflict (and for added/removed resolution). */
  body?: string;
  baseBody?: string;
  currentBody?: string;
  proposedBody?: string;
}

/** GitHub-flavoured heading slug (ascii-ish; duplicates get -1, -2, …). */
export function slugifyHeading(text: string, used: Set<string>): string {
  let base = text
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}\s-]/gu, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  if (!base) base = "section";
  let slug = base;
  let n = 1;
  while (used.has(slug)) {
    slug = `${base}-${n++}`;
  }
  used.add(slug);
  return slug;
}

/** Split markdown into sections by `## ` headings. Preamble → Introduction. */
export function splitSections(markdown: string): PlanSection[] {
  const text = markdown ?? "";
  const lines = text.split(/\r?\n/);
  const sections: PlanSection[] = [];
  const used = new Set<string>();

  let currentHeading: string | null = null;
  let currentLines: string[] = [];
  let fence: { char: string; length: number } | null = null;

  const flush = () => {
    if (currentHeading === null && currentLines.every((l) => !l.trim())) {
      currentLines = [];
      return;
    }
    const heading = currentHeading ?? "Introduction";
    const slug = currentHeading === null ? slugifyHeading("Introduction", used) : slugifyHeading(currentHeading, used);
    const body = currentLines.join("\n").replace(/\n+$/, "");
    sections.push({ slug, heading, body });
    currentLines = [];
  };

  for (const line of lines) {
    const marker = /^ {0,3}(`{3,}|~{3,})(.*)$/.exec(line);
    if (fence) {
      currentLines.push(line);
      if (marker && marker[1][0] === fence.char && marker[1].length >= fence.length && !marker[2].trim()) fence = null;
      continue;
    }
    if (marker) {
      fence = { char: marker[1][0], length: marker[1].length };
      currentLines.push(line);
      continue;
    }
    const m = /^##\s+(.+?)\s*$/.exec(line);
    if (m) {
      flush();
      currentHeading = m[1].trim();
      currentLines = [line];
    } else {
      currentLines.push(line);
    }
  }
  flush();

  if (sections.length === 0) {
    return [{ slug: "introduction", heading: "Introduction", body: text.replace(/\n+$/, "") }];
  }
  return sections;
}

/** Reassemble sections in order into a single markdown document. */
export function joinSections(sections: PlanSection[]): string {
  return sections
    .map((s) => s.body.replace(/\n+$/, ""))
    .filter((b) => b.length > 0)
    .join("\n\n")
    .replace(/\n+$/, "") + (sections.length ? "\n" : "");
}

function bySlug(sections: PlanSection[]): Map<string, PlanSection> {
  return new Map(sections.map((s) => [s.slug, s]));
}

/** Per-slug compare of base vs proposed. */
export function diffSections(baseMd: string, proposedMd: string): SectionDiff[] {
  const base = splitSections(baseMd);
  const proposed = splitSections(proposedMd);
  const baseMap = bySlug(base);
  const propMap = bySlug(proposed);

  // Preserve proposed order for added/changed, then removed that only exist in base.
  const ordered: string[] = [];
  for (const s of proposed) if (!ordered.includes(s.slug)) ordered.push(s.slug);
  for (const s of base) if (!ordered.includes(s.slug)) ordered.push(s.slug);

  return ordered.map((slug) => {
    const b = baseMap.get(slug);
    const p = propMap.get(slug);
    const heading = p?.heading ?? b?.heading ?? slug;
    if (b && !p) return { slug, heading, kind: "removed" as const, baseBody: b.body };
    if (!b && p) return { slug, heading, kind: "added" as const, proposedBody: p.body };
    if (b && p && b.body === p.body) {
      return { slug, heading, kind: "unchanged" as const, baseBody: b.body, proposedBody: p.body };
    }
    return {
      slug,
      heading,
      kind: "changed" as const,
      baseBody: b?.body,
      proposedBody: p?.body,
    };
  });
}

/**
 * Three-way per-section merge for stale sign-off.
 * - canonical(current) == canonical(base) → take proposed
 * - proposed == base → take current
 * - else → conflict (caller must pass resolutions)
 */
export function threeWayMerge(
  baseMd: string,
  currentMd: string,
  proposedMd: string,
  resolutions: Record<string, MergeChoice> = {}
): { ok: true; markdown: string; results: SectionMergeResult[] } | { ok: false; conflicts: SectionMergeResult[]; results: SectionMergeResult[] } {
  const base = bySlug(splitSections(baseMd));
  const current = bySlug(splitSections(currentMd));
  const proposed = bySlug(splitSections(proposedMd));
  const slugs = [...new Set([...base.keys(), ...current.keys(), ...proposed.keys()])];

  // Order: current doc order, then anything only in proposed, then only in base.
  const ordered: string[] = [];
  for (const s of splitSections(currentMd)) if (!ordered.includes(s.slug)) ordered.push(s.slug);
  for (const s of splitSections(proposedMd)) if (!ordered.includes(s.slug)) ordered.push(s.slug);
  for (const s of splitSections(baseMd)) if (!ordered.includes(s.slug)) ordered.push(s.slug);
  for (const slug of slugs) if (!ordered.includes(slug)) ordered.push(slug);

  const results: SectionMergeResult[] = [];
  const outSections: PlanSection[] = [];
  const conflicts: SectionMergeResult[] = [];

  for (const slug of ordered) {
    const b = base.get(slug);
    const c = current.get(slug);
    const p = proposed.get(slug);
    const heading = c?.heading ?? p?.heading ?? b?.heading ?? slug;
    const baseBody = b?.body;
    const currentBody = c?.body;
    const proposedBody = p?.body;

    // Section only in base (removed from both current and proposed) — drop.
    if (b && !c && !p) continue;

    // Added only in proposed, not in current.
    if (!b && !c && p) {
      const r: SectionMergeResult = { slug, heading, status: "added", body: p.body, proposedBody: p.body };
      results.push(r);
      outSections.push({ slug, heading, body: p.body });
      continue;
    }

    // In current but never in base or proposed — keep current.
    if (!b && c && !p) {
      const r: SectionMergeResult = { slug, heading, status: "unchanged", body: c.body, currentBody: c.body };
      results.push(r);
      outSections.push({ slug, heading, body: c.body });
      continue;
    }

    // Removed from proposed (was in base), still in current.
    if (b && c && !p) {
      if (c.body === b.body) {
        // Clean remove — drop section.
        results.push({ slug, heading, status: "removed", baseBody, currentBody });
        continue;
      }
      // Current diverged — conflict (keep or drop).
      const choice = resolutions[slug];
      if (!choice) {
        const r: SectionMergeResult = {
          slug, heading, status: "conflict",
          baseBody, currentBody, proposedBody: undefined,
        };
        results.push(r);
        conflicts.push(r);
        continue;
      }
      if (choice === "current") {
        results.push({ slug, heading, status: "take_current", body: c.body, baseBody, currentBody });
        outSections.push({ slug, heading, body: c.body });
      } else {
        results.push({ slug, heading, status: "removed", baseBody, currentBody });
      }
      continue;
    }

    // Added in current after base (not in proposed) — keep current.
    if (b && !c && p) {
      if (p.body === b.body) {
        // Proposal never touched this section and current deleted it — clean removal,
        // not a conflict (proposed == base → current wins).
        results.push({ slug, heading, status: "removed", baseBody, proposedBody: p.body });
        continue;
      }
      // Removed from current but proposed CHANGED it — conflict unless resolved.
      const choice = resolutions[slug];
      if (!choice) {
        const r: SectionMergeResult = {
          slug, heading, status: "conflict",
          baseBody, currentBody: undefined, proposedBody: p.body,
        };
        results.push(r);
        conflicts.push(r);
        continue;
      }
      if (choice === "proposed") {
        results.push({ slug, heading, status: "take_proposed", body: p.body, proposedBody: p.body });
        outSections.push({ slug, heading, body: p.body });
      } else {
        results.push({ slug, heading, status: "removed", proposedBody: p.body });
      }
      continue;
    }

    // All three present (or base+current+proposed variants).
    if (c && p) {
      if (baseBody !== undefined && c.body === baseBody) {
        // Current untouched since base → take proposed.
        results.push({
          slug, heading, status: p.body === baseBody ? "unchanged" : "take_proposed",
          body: p.body, baseBody, currentBody: c.body, proposedBody: p.body,
        });
        outSections.push({ slug, heading, body: p.body });
        continue;
      }
      if (baseBody !== undefined && p.body === baseBody) {
        // Proposal didn't touch this section → keep current.
        results.push({
          slug, heading, status: "take_current",
          body: c.body, baseBody, currentBody: c.body, proposedBody: p.body,
        });
        outSections.push({ slug, heading, body: c.body });
        continue;
      }
      if (c.body === p.body) {
        results.push({
          slug, heading, status: "unchanged",
          body: c.body, baseBody, currentBody: c.body, proposedBody: p.body,
        });
        outSections.push({ slug, heading, body: c.body });
        continue;
      }
      // Both changed → conflict.
      const choice = resolutions[slug];
      if (!choice) {
        const r: SectionMergeResult = {
          slug, heading, status: "conflict",
          baseBody, currentBody: c.body, proposedBody: p.body,
        };
        results.push(r);
        conflicts.push(r);
        continue;
      }
      const body = choice === "proposed" ? p.body : c.body;
      results.push({
        slug, heading,
        status: choice === "proposed" ? "take_proposed" : "take_current",
        body, baseBody, currentBody: c.body, proposedBody: p.body,
      });
      outSections.push({ slug, heading, body });
    }
  }

  if (conflicts.length) return { ok: false, conflicts, results };
  return { ok: true, markdown: joinSections(outSections), results };
}

/** Standard plan outline used by POST /plan/start with template:true. */
export const PLAN_TEMPLATE = `# Plan

## Goal

What success looks like.

## Scope

In scope / out of scope.

## Approach

How we will get there.

## Rollout

Phases, owners, and timing.

## Risks

What could go wrong and how we watch for it.

## Open questions

Unresolved decisions.
`;
